// englishcontent1.js
// Grade9Ready — English Section 1: Understanding Fiction & Non-Fiction
//
// Production-improved version:
// - Keeps all 17 lesson topics
// - Adds consistent GCSE-ready metadata
// - Adds examSkills, quickCheck, commonMistakes and keywords
// - Normalises question types: mcq / true-false / short-answer
// - Keeps tiered worked examples: Weak / Good / Excellent
// - Supports AI feedback, mastery, progress tracking and lesson rendering

const EXAM_SKILLS = {
  AO1: 'AO1: identify and interpret explicit and implicit information and ideas; select evidence',
  AO2: 'AO2: explain, comment on and analyse how writers use language and structure to achieve effects',
  AO3: 'AO3: compare writers’ ideas and perspectives and consider context where relevant',
  AO4: 'AO4: evaluate texts critically and support views with textual references',
  AO5: 'AO5: communicate clearly and imaginatively, adapting tone, style and register for purpose and audience',
  AO6: 'AO6: use a range of vocabulary and sentence structures with accurate spelling and punctuation',
};

const sectionCommonMistakes = [
  'Naming a technique without explaining its effect',
  'Using long quotations instead of short embedded evidence',
  'Making an inference without linking it to a specific word or phrase',
  'Confusing purpose, audience, tone, register and style',
  'Retelling what happens instead of analysing how the writer creates meaning',
];

const tier = (label, text, critique) => ({ label, text, critique });

const makeTopic = (topic) => ({
  duration: '30-45 mins',
  xp: 120,
  difficulty: 'foundation-to-grade-9',
  gradeTarget: topic.gradeTarget || '4-9',
  stretchTarget: topic.stretchTarget || '8-9',
  curriculumStage: 'KS3 foundation into GCSE English Language',
  examBoardFit: ['AQA', 'Edexcel', 'OCR', 'Eduqas/WJEC'],
  examSkills: topic.examSkills || [EXAM_SKILLS.AO1, EXAM_SKILLS.AO2],
  commonMistakes: topic.commonMistakes || sectionCommonMistakes,
  quickCheck: topic.quickCheck || [],
  keywords: topic.keywords || [],
  retrievalStrength: topic.retrievalStrength || 'core',
  ...topic,
});

export const englishContent1 = {
  id: 1,
  title: 'Understanding Fiction & Non-Fiction',
  slug: 'understanding-fiction-and-non-fiction',
  color: 'blue',
  icon: 'BookOpen',
  description:
    'The foundation section for English Language and Literature: text types, purpose, audience, inference, language, tone, context, grammar and exam-ready analytical writing.',
  curriculumLinks: [
    'Read a wide range of fiction and non-fiction',
    'Make inferences and refer to evidence in the text',
    'Understand purpose, audience and context',
    'Analyse language, vocabulary choice, grammar, structure and organisational features',
    'Use accurate grammar, punctuation and Standard English',
    'Write for a wide range of purposes and audiences',
  ],
  sectionSkills: [
    EXAM_SKILLS.AO1,
    EXAM_SKILLS.AO2,
    EXAM_SKILLS.AO5,
    EXAM_SKILLS.AO6,
  ],
  topics: [
    makeTopic({
      id: 'eng-1-1',
      slug: 'different-types-of-text',
      title: 'Different Types of Text',
      summary: 'Recognise fiction, non-fiction and their key forms.',
      keywords: ['fiction', 'non-fiction', 'form', 'genre', 'novel', 'article', 'biography'],
      examSkills: [EXAM_SKILLS.AO1],
      quickCheck: [
        'What is fiction?',
        'What is non-fiction?',
        'Why can some texts blur the line between fiction and non-fiction?',
      ],
      commonMistakes: [
        'Calling every book fiction',
        'Assuming “based on true events” automatically means non-fiction',
        'Naming the broad category but not the specific form',
      ],
      explainer: {
        title: 'Different Types of Text',
        content: `
All texts fall into two broad categories: fiction and non-fiction.

Fiction is writing made up by the writer. It usually tells a story, invents characters, and explores ideas through imagination.

Common fiction forms include:
- Novels: full-length imagined stories
- Short stories: shorter complete narratives
- Novellas: longer than a short story but shorter than a novel
- Plays: stories written for performance
- Poems: often imaginative, emotional or reflective writing
- Fables, myths and legends: traditional stories, often with symbolic meanings
- Graphic novels and comics: stories told through words and images

Non-fiction is based on real events, people, places or ideas. Its job is usually to inform, explain, persuade, argue, describe or advise.

Common non-fiction forms include:
- Newspaper and magazine articles
- Biographies and autobiographies
- Letters and emails
- Diaries and journals
- Speeches, reviews, reports and blogs
- Leaflets and advertisements
- Travel writing and memoirs

Some texts blur the line. A historical novel is fiction inspired by real events. A memoir is non-fiction that uses storytelling techniques. When in doubt, ask: is the writer claiming this really happened?
`,
      },
      interactiveLesson: {
        title: 'Fiction vs Non-Fiction',
        steps: [
          {
            type: 'info',
            content:
              'For each example, decide whether the text is fiction or non-fiction. Then think about the exact form.',
          },
          {
            type: 'question',
            question: 'A biography of Nelson Mandela:',
            options: ['Fiction', 'Non-fiction'],
            correct: 1,
            explanation: 'Biographies tell the real life story of a real person.',
          },
          {
            type: 'question',
            question: '"Harry Potter and the Philosopher’s Stone":',
            options: ['Fiction', 'Non-fiction'],
            correct: 0,
            explanation: 'It is a novel: a long, imagined story.',
          },
          {
            type: 'question',
            question: 'An instruction manual for a washing machine:',
            options: ['Fiction', 'Non-fiction'],
            correct: 1,
            explanation: 'It gives real instructions for a practical task.',
          },
        ],
      },
      workedExamples: [
        {
          label: 'Example 1 — Spotting the form',
          prompt:
            'Text: "Dear Diary, today we arrived in London after a 14-hour flight. The airport was chaos…"\n\nTask: Identify the form and classify the text.',
          tiers: [
            tier('Weak', 'It is non-fiction.', 'Correct label but no explanation or evidence.'),
            tier(
              'Good',
              'This is non-fiction. It is a diary entry because it is written in the first person and records a real trip.',
              'Correct, identifies the form and gives one supporting reason.'
            ),
            tier(
              'Excellent',
              'This is non-fiction in the form of a diary entry. The first-person voice ("we arrived"), direct address to the diary, and real-world detail ("14-hour flight") show the writer is recording their own experience rather than inventing a story.',
              'Names the form, classifies confidently, and quotes precise evidence.'
            ),
          ],
        },
        {
          label: 'Example 2 — A blurred case',
          prompt:
            'Text: "Based on true events, the novel follows a young nurse through the Blitz of 1940."\n\nTask: Is this fiction or non-fiction? Explain.',
          tiers: [
            tier(
              'Weak',
              'It is true so it is non-fiction.',
              'Misses the key word "novel". “Based on” is not the same as “true account”.'
            ),
            tier(
              'Good',
              'This is fiction. It is a novel, so the characters and story are made up, even though it is "based on true events".',
              'Correct, and explains why “based on” does not make it non-fiction.'
            ),
            tier(
              'Excellent',
              'This is fiction. The word "novel" tells us it is an imagined story; "based on true events" only means the writer has drawn inspiration from real history. A reader should enjoy the story but should not treat every character or scene as factual.',
              'Confident classification with awareness of the “based on true events” convention.'
            ),
          ],
        },
      ],
      practice: [
        { type: 'mcq', question: 'A news article about a local election:', options: ['Fiction', 'Non-fiction'], correct: 1 },
        { type: 'mcq', question: 'A fable about a clever fox:', options: ['Fiction', 'Non-fiction'], correct: 0 },
        { type: 'mcq', question: 'A haiku about winter:', options: ['Usually fiction or literary writing', 'A factual report'], correct: 0 },
        { type: 'mcq', question: 'A restaurant review in a Sunday paper:', options: ['Fiction', 'Non-fiction'], correct: 1 },
        { type: 'true-false', question: 'A graphic novel can count as fiction even though it uses pictures.', correct: true },
      ],
      applicationTask: {
        title: 'Application Task',
        prompt:
          'Pick any text you have read recently: a book, article, blog, leaflet, review or post. In 3–4 sentences, state whether it is fiction or non-fiction, name its form, and justify your answer.',
        sentenceStarter: 'The text I chose is...',
        hints: ['Name the form precisely.', 'Give one piece of textual evidence.', 'Avoid simply saying “book” or “website”.'],
      },
      aiFeedback: {
        successCriteria: ['Correctly classifies the text', 'Names the specific form', 'Uses evidence to justify the answer'],
      },
      improvementTask: {
        title: 'Improve Your Answer',
        prompt:
          'Improve your answer by adding one short quotation and naming the form more precisely, such as “novel”, “memoir”, “review”, “article” or “speech”.',
      },
      mastery: {
        title: 'Mastery Challenge',
        prompt: 'Explain how a memoir can be non-fiction even though it uses storytelling techniques.',
        xpReward: 50,
      },
    }),

    makeTopic({
      id: 'eng-1-2',
      slug: 'purpose-and-audience',
      title: 'Purpose and Audience',
      summary: 'Decode why a writer writes and who they write for.',
      keywords: ['purpose', 'audience', 'PAPER', 'persuade', 'argue', 'inform', 'explain'],
      examSkills: [EXAM_SKILLS.AO1, EXAM_SKILLS.AO2, EXAM_SKILLS.AO5],
      quickCheck: [
        'What does purpose mean?',
        'What does audience mean?',
        'How can vocabulary reveal the intended audience?',
      ],
      commonMistakes: [
        'Giving only one purpose when a text has more than one',
        'Saying “everyone” for audience without evidence',
        'Ignoring layout and vocabulary when identifying audience',
      ],
      explainer: {
        title: 'Purpose and Audience',
        content: `
Every text is written for a reason and aimed at someone. Writers do not just put words on a page — they make deliberate choices about both purpose and audience.

Common purposes can be remembered as PAPER plus describe and explain:

- Persuade: change the reader’s view or behaviour
- Argue: present a case with reasons and evidence
- Inform: give facts clearly
- Entertain: interest, amuse or emotionally engage
- Advise: recommend what someone should do
- Describe: create a vivid picture
- Explain: show how or why something happens

A single text often has more than one purpose. A magazine article might inform and entertain. A charity advert might inform, persuade and emotionally affect the reader.

Audience means the intended reader, listener or viewer. Think about:
- Age: children, teenagers, adults
- Expertise: beginners, general readers, specialists
- Values and interests
- Cultural background
- How much time they have
- What the writer wants them to think, feel or do

Audience affects:
- Vocabulary: technical or everyday
- Tone: formal, chatty, urgent or serious
- Examples: relatable, academic, emotional or practical
- Layout: dense paragraphs, headings, images or bullet points
- Length: brief and scannable or detailed and complex

When analysing purpose and audience, ask:
- What is the writer trying to achieve?
- Who is the writer addressing?
- Which language, layout or structural choices prove this?
`,
      },
      interactiveLesson: {
        title: 'Spot the Purpose and Audience',
        steps: [
          { type: 'info', content: 'Identify purpose and audience from the clues in the language.' },
          {
            type: 'question',
            question: '"Buy now — limited time only!" The main purpose is to:',
            options: ['Inform', 'Persuade'],
            correct: 1,
            explanation: 'The imperative “Buy” and urgency of “limited time” push the reader to act.',
          },
          {
            type: 'question',
            question: '"The amygdala regulates fear response in the brain." The audience is most likely:',
            options: ['Medical students or interested adult readers', 'Very young children'],
            correct: 0,
            explanation: 'The technical vocabulary suggests a more specialised or mature audience.',
          },
          {
            type: 'question',
            question: '"Once upon a time, in a kingdom far away..." The purpose is mainly to:',
            options: ['Entertain', 'Instruct'],
            correct: 0,
          },
        ],
      },
      workedExamples: [
        {
          label: 'Example 1 — Single purpose',
          prompt:
            'Text: "Recycling saves energy. One recycled tin can powers a TV for three hours."\n\nTask: Identify purpose and audience.',
          tiers: [
            tier('Weak', 'It is informing.', 'Correct purpose but no audience comment or evidence.'),
            tier(
              'Good',
              'The purpose is to inform. The audience is the general public because the fact about the TV is everyday and easy to follow.',
              'Both purpose and audience are identified, with one piece of reasoning.'
            ),
            tier(
              'Excellent',
              'The main purpose is to inform, but there is also a persuasive edge: the striking statistic "three hours" encourages the reader to recycle. The audience is the general public, shown by the familiar comparison to a TV rather than a technical measurement such as kilowatt-hours.',
              'Nuanced: notices two purposes and explains the everyday vocabulary as audience evidence.'
            ),
          ],
        },
        {
          label: 'Example 2 — Dual purpose',
          prompt:
            'Text: A BBC Bitesize article on World War I uses cartoons, short paragraphs and quick quiz questions.\n\nTask: What are the purpose(s) and audience?',
          tiers: [
            tier('Weak', 'It is to inform school children.', 'Touches on purpose and audience but gives no reasoning.'),
            tier(
              'Good',
              'The purpose is to inform and entertain school students. The cartoons and quizzes suggest a young audience.',
              'Both purposes identified with simple reasoning.'
            ),
            tier(
              'Excellent',
              'The article informs and entertains. Short paragraphs suit readers who need information broken into manageable chunks, while cartoons and quizzes make learning feel active and playful. This points to a school-age audience, probably Key Stage 3 or early GCSE students.',
              'Identifies dual purpose, uses layout evidence and links it to audience needs.'
            ),
          ],
        },
      ],
      practice: [
        { type: 'mcq', question: 'A travel blog describing a market in Marrakesh:', options: ['Describe and entertain', 'Argue only'], correct: 0 },
        { type: 'mcq', question: 'A letter to the council demanding a new crossing:', options: ['Argue and persuade', 'Entertain only'], correct: 0 },
        { type: 'mcq', question: 'A recipe for pancakes:', options: ['Explain and advise', 'Persuade only'], correct: 0 },
        { type: 'true-false', question: 'A text can only have one purpose.', correct: false },
        { type: 'true-false', question: 'The audience affects the vocabulary a writer uses.', correct: true },
      ],
      applicationTask: {
        title: 'Application Task',
        prompt:
          'Pick a text from daily life: an advert, social post, school newsletter, leaflet or website. In 3–4 sentences, identify its purpose and audience with evidence.',
        sentenceStarter: 'The purpose of this text is...',
        hints: ['Quote one phrase as evidence.', 'Comment on vocabulary, layout or tone.', 'Try to identify more than one purpose if appropriate.'],
      },
      aiFeedback: {
        successCriteria: ['Identifies purpose with evidence', 'Identifies audience with evidence', 'Comments on vocabulary, layout or tone'],
      },
      improvementTask: {
        title: 'Improve Your Answer',
        prompt: 'Improve your answer by showing how one language choice reveals the audience.',
      },
      mastery: {
        title: 'Mastery Challenge',
        prompt:
          'Why might a writer deliberately hide their real purpose, for example an advert disguised as an informative article?',
        xpReward: 50,
      },
    }),

    makeTopic({
      id: 'eng-1-3',
      slug: 'language-and-inference',
      title: 'Language and Inference',
      summary: 'Understand how language suggests ideas beyond the surface.',
      keywords: ['inference', 'implicit', 'explicit', 'connotation', 'evidence', 'PEE'],
      examSkills: [EXAM_SKILLS.AO1, EXAM_SKILLS.AO2],
      quickCheck: [
        'What is the difference between explicit and implicit meaning?',
        'Why should inferences be supported with evidence?',
        'What does connotation mean?',
      ],
      commonMistakes: [
        'Guessing without evidence',
        'Using vague emotions such as “sad” or “happy” when a more precise word is needed',
        'Explaining what happens instead of what is suggested',
      ],
      explainer: {
        title: 'Language and Inference',
        content: `
Inference means working out something that is suggested but not directly stated. It is the difference between what a text says and what it means.

Explicit meaning is stated directly:
- "She was angry."

Implicit meaning is hinted:
- "She slammed the door and refused to look back."

Writers often hint rather than tell:
- "She slammed the door" suggests anger.
- "He whispered, eyes darting" suggests fear or secrecy.
- "The smile vanished from his face" suggests disappointment, shock or hurt.

Writers create inference through:
- Action: what characters do
- Dialogue: what people say and how they say it
- Word choice: connotation and emotional associations
- Body language: posture, movement, facial expression
- Setting details: weather, light, sound, objects
- Silence: what a character avoids saying

To explain an inference, use a clear analytical structure:
- Point: what you infer
- Evidence: the short quotation that supports it
- Explanation: how the evidence proves the inference

Strong inferences feel inevitable. Once you name them, the reader thinks: “Yes, that makes sense.”
`,
      },
      interactiveLesson: {
        title: 'Reading Between the Lines',
        steps: [
          {
            type: 'question',
            question: '"Amir slammed the door and didn’t say a word." What can we infer?',
            options: ['He is angry or upset', 'He is excited'],
            correct: 0,
          },
          {
            type: 'question',
            question: '"She whispered and looked around nervously." What can we infer?',
            options: ['She is afraid or worried', 'She is bored'],
            correct: 0,
          },
          {
            type: 'question',
            question: '"He forced a smile but his eyes stayed cold." What can we infer?',
            options: ['His real feelings are different from his expression', 'He is genuinely happy'],
            correct: 0,
          },
        ],
      },
      workedExamples: [
        {
          label: 'Example 1 — Inferring emotion',
          prompt:
            'Text: "She gripped the letter so tightly that her knuckles turned white."\n\nTask: What can we infer about her feelings?',
          tiers: [
            tier('Weak', 'She feels something.', 'Too vague. No clear inference is made.'),
            tier(
              'Good',
              'She is feeling angry or tense. The word "gripped" shows she is holding the letter tightly.',
              'Clear inference supported by one word.'
            ),
            tier(
              'Excellent',
              'She is deeply tense, perhaps furious or frightened. "Gripped" is far stronger than "held", and "knuckles turned white" shows the force physically without the writer naming the emotion. The reader is invited to feel her tension through her body.',
              'Precise inference, zooms in on one word, and comments on writer’s craft.'
            ),
          ],
        },
        {
          label: 'Example 2 — Inferring relationship',
          prompt:
            'Text: "When he entered, she kept her eyes on her book and turned the page a little too loudly."\n\nTask: What can we infer about their relationship?',
          tiers: [
            tier('Weak', 'They do not get on.', 'A reasonable guess but no evidence is used.'),
            tier(
              'Good',
              'They are not on good terms. She avoids looking at him and the noisy page suggests she is annoyed.',
              'Correct with evidence.'
            ),
            tier(
              'Excellent',
              'There is unspoken tension between them. "Kept her eyes on her book" avoids any greeting, while "a little too loudly" suggests passive aggression: the noise replaces the argument. The writer lets the reader infer emotional coldness without directly explaining it.',
              'Identifies passive aggression and explains the effect of implication over statement.'
            ),
          ],
        },
      ],
      practice: [
        { type: 'mcq', question: '"He checked his watch every two minutes." We can infer he is:', options: ['Impatient or anxious', 'Relaxed'], correct: 0 },
        { type: 'mcq', question: '"She laughed a little too loudly at his joke." We can infer she is:', options: ['Nervous or pretending', 'Bored'], correct: 0 },
        { type: 'mcq', question: '"The plate was licked clean." We can infer the meal was:', options: ['Enjoyed', 'Disliked'], correct: 0 },
        { type: 'true-false', question: 'Inference is what a text says directly.', correct: false },
        { type: 'true-false', question: 'Strong writing often implies feelings rather than stating them.', correct: true },
      ],
      applicationTask: {
        title: 'Application Task',
        prompt:
          'Read: "He placed the photo face down on the shelf and walked out." What can we infer about his feelings towards the person in the photo? Write 3 sentences.',
        sentenceStarter: 'We can infer...',
        hints: ['Use "face down" as evidence.', 'Comment on what the action suggests.', 'Name a precise emotion such as grief, regret, anger or betrayal.'],
      },
      aiFeedback: {
        successCriteria: ['Makes a clear inference', 'Uses a short quotation', 'Explains why the evidence supports the inference'],
      },
      improvementTask: {
        title: 'Improve Your Answer',
        prompt: 'Improve by naming a specific emotion rather than a vague one.',
      },
      mastery: {
        title: 'Mastery Challenge',
        prompt: 'Why do writers trust readers to infer feelings rather than stating them outright?',
        xpReward: 50,
      },
    }),

    makeTopic({
      id: 'eng-1-4',
      slug: 'layout',
      title: 'Layout',
      summary: 'Decode how presentation and visual structure shape reading.',
      keywords: ['layout', 'headline', 'subheading', 'caption', 'pull-quote', 'white space', 'columns'],
      examSkills: [EXAM_SKILLS.AO2, EXAM_SKILLS.AO5],
      quickCheck: [
        'What is layout?',
        'How do subheadings help a reader?',
        'Why is white space not just empty space?',
      ],
      commonMistakes: [
        'Listing layout features without explaining their effect',
        'Ignoring audience when analysing layout',
        'Assuming layout is decoration rather than meaning-making',
      ],
      explainer: {
        title: 'Layout',
        content: `
Layout is how a text is physically arranged on the page or screen. It is not just decoration. Layout controls what the reader sees first, how quickly they understand the text, and how seriously they take it.

Key layout features include:
- Title or headline: grabs attention and signals the topic
- Subheadings: divide the text and help readers scan
- Paragraphs: group related ideas
- Columns: common in newspapers and magazines
- Bullet points and numbered lists: make information easy to process
- Captions: explain images concisely
- Pull-quotes: highlight powerful lines from the body text
- Bold, italics, underline and colour: emphasise key information
- Font size and style: create hierarchy and tone
- Images, diagrams, graphs and infographics: add visual meaning
- White space: gives the eye rest and can make the text feel cleaner and more professional

Fiction usually uses fewer layout tricks, although chapters, paragraph breaks, line breaks, italics and unusual spacing can still be important.

Non-fiction, especially digital, journalistic and persuasive texts, often uses layout heavily. Adverts, leaflets, web pages and tabloid headlines use layout to compete for attention.

Always ask: what is this layout feature trying to do for the reader?
`,
      },
      interactiveLesson: {
        title: 'Layout Features',
        steps: [
          { type: 'question', question: 'A quote pulled out and enlarged within an article is a:', options: ['Pull-quote', 'Caption'], correct: 0 },
          { type: 'question', question: 'Bullet points are most useful for:', options: ['A scannable list of steps', 'Building atmosphere in a novel'], correct: 0 },
          { type: 'question', question: 'Bold text is typically used to:', options: ['Emphasise key words', 'Signal quiet speech'], correct: 0 },
        ],
      },
      workedExamples: [
        {
          label: 'Example 1 — Non-fiction leaflet',
          prompt:
            'A health leaflet uses a large title, a colourful photo, three subheadings, bullet points and a pull-quote.\n\nTask: Explain the effect of any two layout features.',
          tiers: [
            tier('Weak', 'The title is big and the bullets are there.', 'Names features without explaining effect.'),
            tier(
              'Good',
              'The large title grabs attention. The bullet points make the advice easy to scan quickly.',
              'Two features explained simply.'
            ),
            tier(
              'Excellent',
              'The large title and colourful photo grab attention before the reader processes the detail, which is useful when a leaflet is picked up casually. The bullet points then let the reader skim key advice in seconds, suiting a busy audience who may not read from start to finish.',
              'Each feature is linked to purpose and audience.'
            ),
          ],
        },
        {
          label: 'Example 2 — Newspaper article',
          prompt:
            'A broadsheet article uses columns, a headline, a subheading, a black-and-white photograph with a caption, and short paragraphs.\n\nTask: How does the layout support the purpose of informing?',
          tiers: [
            tier('Weak', 'It has columns and photos.', 'No analysis.'),
            tier(
              'Good',
              'Columns fit more text on the page and the caption explains the photo, which helps the reader understand the story.',
              'Two features briefly analysed.'
            ),
            tier(
              'Excellent',
              'Short paragraphs and columns let the reader scan the main points even in a busy context, such as a commute. The subheading summarises the story in one line, so even a reader who does not finish the article still leaves informed. The captioned photograph grounds the events in reality, adding credibility.',
              'Layered analysis that links layout to purpose and context.'
            ),
          ],
        },
      ],
      practice: [
        { type: 'mcq', question: 'Which feature helps a reader jump to the section they care about?', options: ['Subheadings', 'Justified alignment'], correct: 0 },
        { type: 'mcq', question: 'Which feature is most persuasive when placed near a headline?', options: ['A striking image', 'A long footnote'], correct: 0 },
        { type: 'mcq', question: 'Pull-quotes are most common in:', options: ['Magazine features', 'Instruction manuals'], correct: 0 },
        { type: 'true-false', question: 'White space is a deliberate layout choice.', correct: true },
        { type: 'true-false', question: 'Fiction never uses layout features.', correct: false },
      ],
      applicationTask: {
        title: 'Application Task',
        prompt:
          'Describe the layout of a leaflet aimed at teenagers encouraging them to exercise. Name three features and explain the effect of each.',
        sentenceStarter: 'The leaflet would use...',
        hints: ['Match each feature to the audience.', 'Link each feature to a reader effect.', 'Think about colour, images, headings and scannability.'],
      },
      aiFeedback: {
        successCriteria: ['Names three specific features', 'Explains the effect of each', 'Considers the teenage audience'],
      },
      improvementTask: {
        title: 'Improve Your Answer',
        prompt: 'Add one feature students often miss, such as colour psychology, font choice or white space.',
      },
      mastery: {
        title: 'Mastery Challenge',
        prompt: 'Why can a well-designed leaflet persuade more effectively than a longer plain-text article?',
        xpReward: 50,
      },
    }),

    makeTopic({
      id: 'eng-1-5',
      slug: 'tone-register-and-style',
      title: 'Tone, Register and Style',
      summary: 'Tell voice, register and personal style apart — and use them.',
      keywords: ['tone', 'register', 'style', 'formal', 'informal', 'voice', 'attitude'],
      examSkills: [EXAM_SKILLS.AO2, EXAM_SKILLS.AO5],
      quickCheck: [
        'What is tone?',
        'What is register?',
        'How is style different from tone?',
      ],
      commonMistakes: [
        'Confusing tone with register',
        'Calling every informal tone “chatty” without evidence',
        'Saying “style” when you mean “subject”',
      ],
      explainer: {
        title: 'Tone, Register and Style',
        content: `
Tone, register and style are related, but they are not the same.

Tone is the writer’s attitude towards the subject, reader or situation. Tone can be:
- Serious
- Angry
- Reassuring
- Joyful
- Hopeful
- Melancholy
- Mocking
- Sarcastic
- Nostalgic
- Urgent
- Detached

Tone is created through vocabulary, punctuation, sentence length, imagery and structure.

Register is the level of formality. It usually sits on a scale:
- Very formal: legal documents, academic essays
- Formal: reports, broadsheet journalism, letters to officials
- Neutral: textbooks, news bulletins
- Informal: friendly letters, blogs, lifestyle articles
- Very informal: text messages and some social media posts

Register depends on audience and context. A serious subject can still be discussed informally, and a light topic can still be written formally.

Style is the writer’s overall way of writing: their fingerprint. It includes:
- Tone
- Register
- Vocabulary patterns
- Sentence structure
- Favourite techniques
- Imagery
- Rhythm
- Repeated habits across different pieces

Useful questions:
- What tone is being used?
- Where does the register sit on the formality scale?
- What makes this writer’s style recognisable?
`,
      },
      interactiveLesson: {
        title: 'Identify Tone and Register',
        steps: [
          { type: 'question', question: '"Another Monday. Brilliant." The tone is:', options: ['Sarcastic', 'Joyful'], correct: 0 },
          { type: 'question', question: 'A letter beginning "Dear Sir/Madam, I am writing to..." uses which register?', options: ['Very formal', 'Very informal'], correct: 0 },
          { type: 'question', question: 'Style is best described as:', options: ['A writer’s unique voice over time', 'The topic of one paragraph'], correct: 0 },
        ],
      },
      workedExamples: [
        {
          label: 'Example 1 — Identifying tone',
          prompt:
            '"Honestly, I can think of nothing more exciting than a Monday morning staff meeting."\n\nTask: Identify the tone, with evidence.',
          tiers: [
            tier('Weak', 'The tone is bored.', 'Right general feeling but no evidence.'),
            tier(
              'Good',
              'The tone is sarcastic. "Nothing more exciting" is meant ironically because a Monday staff meeting is usually seen as dull.',
              'Correct tone with basic reasoning.'
            ),
            tier(
              'Excellent',
              'The tone is sarcastic and dryly humorous. The phrase "nothing more exciting" is exaggerated praise placed against a deliberately dull event. The word "Honestly" acts as a tongue-in-cheek cue, inviting the reader to spot the gap between the words and the real meaning.',
              'Precise tone label, reads irony, and notices a cue word.'
            ),
          ],
        },
        {
          label: 'Example 2 — Matching register to audience',
          prompt:
            'Two openings for a piece about climate change:\nA) "Mate, we are in serious trouble with the planet — and fast."\nB) "Anthropogenic climate change poses an existential risk to human civilisation."\n\nTask: Compare the register and audience.',
          tiers: [
            tier('Weak', 'A is casual and B is formal.', 'Correct but no audience link.'),
            tier(
              'Good',
              'A is informal because of direct address and slang ("Mate"). B is very formal because of precise scientific vocabulary. A suits a teen blog; B suits an academic or policy audience.',
              'Register and audience both named.'
            ),
            tier(
              'Excellent',
              'A sits near the informal end of the register scale: "Mate" and the conversational dash suggest a peer talking to peers, perhaps in a blog or podcast. B is highly formal: phrases such as "anthropogenic climate change" and "existential risk" signal an academic or policy audience who expect precision. Both address the same crisis but assume very different readers.',
              'Uses register scale, technical vocabulary and audience expectation.'
            ),
          ],
        },
      ],
      practice: [
        { type: 'mcq', question: '"Let us now consider the implications..." is which register?', options: ['Formal', 'Very informal'], correct: 0 },
        { type: 'mcq', question: '"OMG you wouldn’t believe what happened!" is which register?', options: ['Very informal', 'Formal'], correct: 0 },
        { type: 'mcq', question: 'A writer who often uses lists of three and short punchy verbs is showing:', options: ['Style', 'Register only'], correct: 0 },
        { type: 'true-false', question: 'Tone and register are the same thing.', correct: false },
        { type: 'true-false', question: 'Style can be consistent across a writer’s different pieces.', correct: true },
      ],
      applicationTask: {
        title: 'Application Task',
        prompt:
          'Read this line: "You should definitely come — it’s going to be the best night EVER!!" Describe the tone, register and clues to the writer’s style.',
        sentenceStarter: 'The tone is...',
        hints: ['Pick words as evidence.', 'Place the register on the formality scale.', 'Comment on punctuation and capital letters.'],
      },
      aiFeedback: {
        successCriteria: ['Identifies tone with evidence', 'Places register on the formality scale', 'Makes one style observation'],
      },
      improvementTask: {
        title: 'Improve Your Answer',
        prompt:
          'Improve by explaining how punctuation, the dash, the double exclamation mark and the capitalised “EVER” contribute to the effect.',
      },
      mastery: {
        title: 'Mastery Challenge',
        prompt: 'Why is “very formal” not automatically better than “informal”? Give an example where informal is the stronger choice.',
        xpReward: 50,
      },
    }),

    makeTopic({
      id: 'eng-1-6',
      slug: 'informal-style',
      title: 'Informal Style',
      summary: 'Recognise, analyse and use informal writing features.',
      keywords: ['informal', 'slang', 'contractions', 'direct address', 'rhetorical question', 'colloquial'],
      examSkills: [EXAM_SKILLS.AO2, EXAM_SKILLS.AO5],
      quickCheck: [
        'What is a contraction?',
        'Why does direct address suit informal writing?',
        'When can informal writing become unsuitable?',
      ],
      commonMistakes: [
        'Thinking informal writing is automatically wrong',
        'Overusing slang so the writing becomes unclear',
        'Using informal style in formal exam essays',
      ],
      explainer: {
        title: 'Informal Style',
        content: `
Informal writing sounds relaxed, personal and often speech-like. It can be warm, energetic and easy to connect with.

Features of informal writing include:
- Contractions: I’m, don’t, we’ll, it’s
- First-person and second-person pronouns: I, we, you
- Slang and colloquial expressions: cool, gutted, loads, buzzing
- Direct address: imagine you’re standing there
- Rhetorical questions: haven’t we all been there?
- Short, punchy sentences
- Dashes and ellipses for conversational flow
- Exclamation marks for reaction
- Brand names, pop culture references and cultural shorthand
- Abbreviations: uni, TV, app, mini-break

Informal writing is used in:
- Personal messages and emails
- Blogs and vlogs
- Magazine lifestyle articles
- Podcast scripts
- Some newspaper columns
- Social media posts

Risks of going too informal:
- Slang can date quickly
- Emojis and all-caps can look unserious
- The audience may feel excluded if they do not understand the references
- Meaning can become vague or lazy

Informal style is not a mistake. It is a deliberate choice. The key question is whether it suits the audience, purpose and context.
`,
      },
      interactiveLesson: {
        title: 'Spotting Informal Features',
        steps: [
          { type: 'question', question: 'Which is the most informal opening?', options: ['"Hiya guys, you will not BELIEVE this!"', '"Good afternoon. Today I will discuss..."'], correct: 0 },
          { type: 'question', question: 'Which is a contraction?', options: ['We will', 'We’ll'], correct: 1 },
          { type: 'question', question: 'Which word is slang?', options: ['Delighted', 'Buzzing'], correct: 1 },
        ],
      },
      workedExamples: [
        {
          label: 'Example 1 — Identifying informal features',
          prompt: '"Guys, you won’t believe what just happened — it’s wild!"\n\nTask: List the informal features and their effect.',
          tiers: [
            tier('Weak', 'It is informal because it uses slang.', 'Only one feature, no effect.'),
            tier(
              'Good',
              'It uses the direct address "Guys", the contraction "won’t", and the slang "wild". This makes it sound like speech to a friend.',
              'Three features named with an effect.'
            ),
            tier(
              'Excellent',
              'The writer uses direct address ("Guys"), a contraction ("won’t"), a dash for conversational rhythm, and the slang adjective "wild". Together they make the piece sound like a spontaneous voice note, pulling the reader into the writer’s surprise in real time.',
              'Multiple features linked to one overall reader effect.'
            ),
          ],
        },
        {
          label: 'Example 2 — When informal goes too far',
          prompt: '"OMG SO CRINGE, he literally stood there like a clown. I can’t even."\n\nTask: Why might this informal voice be risky in writing?',
          tiers: [
            tier('Weak', 'It is too slangy.', 'Identifies the issue but not the reason.'),
            tier(
              'Good',
              'The slang ("cringe", "literally", "can’t even") is very current, which means it may date quickly. It may also not work for older audiences.',
              'Names specific risks.'
            ),
            tier(
              'Excellent',
              'The writer leans so hard into current slang that the piece may date quickly and fail with audiences outside the same age group. The all-caps also risks sounding shouty rather than funny. Informal style works when it serves the audience; here it narrows the audience too much.',
              'Connects language choices to audience, shelf-life and tone.'
            ),
          ],
        },
      ],
      practice: [
        { type: 'mcq', question: 'Which is informal?', options: ['"Hiya!"', '"Good day to you."'], correct: 0 },
        { type: 'mcq', question: 'Which is a rhetorical question typical of informal style?', options: ['"Who doesn’t love pizza?"', '"What is the economic cost of..."'], correct: 0 },
        { type: 'mcq', question: 'Which is most risky in an exam essay?', options: ['Emojis and slang', 'Full stops'], correct: 0 },
        { type: 'true-false', question: 'Informal writing often uses second person: “you”.', correct: true },
        { type: 'true-false', question: 'Informal writing is always wrong.', correct: false },
      ],
      applicationTask: {
        title: 'Application Task',
        prompt:
          'Write two informal sentences a teenager might post online about a film they loved. Then identify one informal feature and name it.',
        sentenceStarter: 'Honestly, ...',
        hints: ['Use a contraction.', 'Use direct address or a rhetorical question.', 'Avoid making it so slang-heavy that it becomes unclear.'],
      },
      aiFeedback: {
        successCriteria: ['Uses at least one clear informal feature', 'Sounds natural', 'Identifies the feature correctly'],
      },
      improvementTask: {
        title: 'Improve Your Answer',
        prompt: 'Add one informal feature that works for the audience but will not date too quickly.',
      },
      mastery: {
        title: 'Mastery Challenge',
        prompt: 'Explain one benefit and one risk of writing informally.',
        xpReward: 50,
      },
    }),

    makeTopic({
      id: 'eng-1-7',
      slug: 'formal-style',
      title: 'Formal Style',
      summary: 'Recognise, analyse and use precise formal writing.',
      keywords: ['formal', 'Standard English', 'modal verbs', 'nominalisation', 'passive voice', 'technical vocabulary'],
      examSkills: [EXAM_SKILLS.AO5, EXAM_SKILLS.AO6],
      quickCheck: [
        'What is Standard English?',
        'Why do formal texts avoid slang?',
        'What is the difference between “don’t” and “do not”?',
      ],
      commonMistakes: [
        'Thinking formal writing means using unnecessarily complicated words',
        'Mixing slang with formal phrases',
        'Using “I think” repeatedly in formal analysis',
      ],
      explainer: {
        title: 'Formal Style',
        content: `
Formal writing sounds measured, precise and professional. It is used when the topic is serious, the audience is unfamiliar, or a public record is needed.

Features of formal writing include:
- No contractions: "do not" rather than "don’t"
- Standard English
- No slang or regional dialect
- Precise subject-specific vocabulary
- Third person where appropriate: "it can be argued"
- Passive voice used selectively: "mistakes were made"
- Modal verbs: may, might, could, would, should
- Nominalisation: turning actions into nouns, such as "a decision was taken"
- Longer, more complex sentences
- Logical connectives: moreover, consequently, however, therefore

Formal writing is expected in:
- Essays and academic work
- Business letters and emails to unfamiliar recipients
- Reports
- Legal documents
- Serious speeches
- Many news reports

Common student mistakes:
- Using "get" when "receive", "obtain" or "acquire" is more precise
- Using "tell" when "inform", "state" or "explain" is stronger
- Slipping into "you" or "I" in academic writing
- Using inflated vocabulary that makes meaning less clear

Formal writing is not pompous. At its best, it is clear, precise and respectful of the reader’s time.
`,
      },
      interactiveLesson: {
        title: 'Identify Formal Features',
        steps: [
          { type: 'question', question: 'Which is most formal?', options: ['The project achieved its intended objectives.', 'The project smashed it.'], correct: 0 },
          { type: 'question', question: 'Which is a modal verb?', options: ['Might', 'Hit'], correct: 0 },
          { type: 'question', question: 'Which is an example of nominalisation?', options: ['"A decision was taken"', '"They decided"'], correct: 0 },
        ],
      },
      workedExamples: [
        {
          label: 'Example 1 — Identifying formality',
          prompt: '"The evidence indicates that further investigation is required."\n\nTask: Why is this formal?',
          tiers: [
            tier('Weak', 'It has no slang.', 'True but thin.'),
            tier(
              'Good',
              'It has no contractions, uses precise vocabulary ("indicates", "investigation"), and avoids personal pronouns.',
              'Correct, three features named.'
            ),
            tier(
              'Excellent',
              'The sentence is formal because it uses precise Latinate vocabulary ("indicates", "investigation"), the passive construction "is required", and no contractions or personal pronouns. The impersonal tone places the focus on the evidence rather than the writer, which suits an academic or legal audience.',
              'Names grammatical features and links to audience.'
            ),
          ],
        },
        {
          label: 'Example 2 — Rewriting informal into formal',
          prompt: 'Informal: "Hey guys, your bins are a total joke — can you sort them?"\n\nTask: Rewrite formally.',
          tiers: [
            tier('Weak', 'Your bins are not working.', 'Starts better but is still incomplete and vague.'),
            tier(
              'Good',
              'Dear Sir or Madam, the current bin provision is inadequate and requires improvement.',
              'Formal, clear and correct.'
            ),
            tier(
              'Excellent',
              'Dear Sir or Madam, I am writing to raise concerns about the current standard of bin provision in our area, which is no longer adequate. I would be grateful if the council could review the current schedule and consider implementing improvements. Yours faithfully, [Name]',
              'Full formal letter structure, polite modal phrasing, no slang.'
            ),
          ],
        },
      ],
      practice: [
        { type: 'mcq', question: 'Which is formal?', options: ['"Children must be accompanied by an adult."', '"Kids gotta be with a grown-up."'], correct: 0 },
        { type: 'mcq', question: 'Which word is most formal?', options: ['Said', 'Articulated'], correct: 1 },
        { type: 'mcq', question: 'Which feature suits a report for the government?', options: ['Passive voice used carefully', 'All-caps shouting'], correct: 0 },
        { type: 'true-false', question: 'Formal writing often uses contractions like “can’t”.', correct: false },
        { type: 'true-false', question: 'Formal writing avoids regional dialect.', correct: true },
      ],
      applicationTask: {
        title: 'Application Task',
        prompt: 'Rewrite in formal style: "Hey everyone, I reckon we need to sort the canteen out."',
        sentenceStarter: 'It is strongly recommended that...',
        hints: ['Remove direct address and slang.', 'Use a modal verb such as “should” or “could”.', 'Be precise about what needs improving.'],
      },
      aiFeedback: {
        successCriteria: ['Removes informal features', 'Uses precise formal vocabulary', 'Sounds measured and appropriate'],
      },
      improvementTask: {
        title: 'Improve Your Answer',
        prompt: 'Swap at least one vague verb for a more precise alternative.',
      },
      mastery: {
        title: 'Mastery Challenge',
        prompt: 'Why can overly formal writing also fail the reader?',
        xpReward: 50,
      },
    }),

    makeTopic({
      id: 'eng-1-8',
      slug: 'figurative-language',
      title: 'Figurative Language',
      summary: 'Spot and analyse similes, metaphors, personification, imagery, hyperbole, alliteration and onomatopoeia.',
      keywords: ['simile', 'metaphor', 'personification', 'imagery', 'hyperbole', 'alliteration', 'onomatopoeia'],
      examSkills: [EXAM_SKILLS.AO2],
      quickCheck: [
        'What is the difference between a simile and a metaphor?',
        'What does personification do?',
        'Why is naming a technique not enough?',
      ],
      commonMistakes: [
        'Calling every comparison a simile',
        'Naming imagery without saying which sense is being used',
        'Forgetting to explain the reader effect',
      ],
      explainer: {
        title: 'Figurative Language',
        content: `
Figurative language is language that does not mean exactly what it says literally. Writers use it to create pictures, feelings and ideas in the reader’s mind.

Seven key techniques:

1. Simile
A comparison using "like" or "as".
Example: "His anger was like a fire in his chest."

2. Metaphor
Saying one thing is another.
Example: "Her voice was music."

3. Personification
Giving human qualities to non-human things.
Example: "The wind whispered through the trees."

4. Imagery
Descriptive language that appeals to the senses: sight, sound, smell, taste and touch.
Example: "The pavement sizzled under a white-hot sun."

5. Hyperbole
Deliberate exaggeration for effect.
Example: "I have told you a million times."

6. Alliteration
Repeating the same opening sound across nearby words.
Example: "Safer streets, stronger schools."

7. Onomatopoeia
Words that imitate sounds.
Example: "Buzz", "hiss", "crack", "thud".

Writers often combine techniques. A line can contain personified imagery, alliterative metaphor or hyperbolic description. Spotting combinations is a sign of stronger analysis.

When analysing figurative language, always ask:
- What technique is it?
- What image or feeling does it create?
- Why might the writer have chosen it?
`,
      },
      interactiveLesson: {
        title: 'Name the Technique',
        steps: [
          { type: 'question', question: '"Her smile was as bright as the sun." This is a:', options: ['Simile', 'Metaphor'], correct: 0 },
          { type: 'question', question: '"Time is a thief." This is a:', options: ['Metaphor', 'Simile'], correct: 0 },
          { type: 'question', question: '"The trees danced in the wind." This is:', options: ['Personification', 'Onomatopoeia'], correct: 0 },
        ],
      },
      workedExamples: [
        {
          label: 'Example 1 — One image, multiple techniques',
          prompt: '"The city was a hungry beast, swallowing every newcomer whole."\n\nTask: Name the techniques and explain the effect.',
          tiers: [
            tier('Weak', 'It is a metaphor.', 'Names one technique but misses the layered effect.'),
            tier(
              'Good',
              'The writer uses a metaphor ("the city was a hungry beast") and personification ("swallowing"). This makes the city feel dangerous.',
              'Two techniques identified with a basic effect.'
            ),
            tier(
              'Excellent',
              'The writer combines metaphor and personification. The city "was a hungry beast" creates an aggressive image, while "swallowing every newcomer whole" animates it. The verb "swallowing" implies people are consumed and erased, making the city feel threatening and alive.',
              'Two techniques, layered analysis, verb-level zoom.'
            ),
          ],
        },
        {
          label: 'Example 2 — Sound techniques',
          prompt: '"The silver surf slapped softly against the sand, hissing back into the sea."\n\nTask: Identify three techniques and explain their combined effect.',
          tiers: [
            tier('Weak', 'There is alliteration with the S.', 'Only names one feature and stops.'),
            tier(
              'Good',
              'There is alliteration ("silver surf slapped softly"), onomatopoeia ("hissing"), and personification ("slapped"). It helps the reader hear the sea.',
              'Three techniques named with simple effect.'
            ),
            tier(
              'Excellent',
              'The sibilant alliteration ("silver surf slapped softly"), onomatopoeia ("hissing") and gentle personification of the waves combine to reproduce the sound of a soft shore. The reader does not just picture the scene; they hear it, creating a calm, almost hypnotic mood.',
              'Names techniques, sensory effect and mood.'
            ),
          ],
        },
        {
          label: 'Example 3 — Hyperbole in context',
          prompt: '"I waited in that queue for a thousand years."\n\nTask: Identify and analyse the technique.',
          tiers: [
            tier('Weak', 'It is an exaggeration.', 'True but unexplained.'),
            tier('Good', 'This is hyperbole. The writer exaggerates to show how long it felt.', 'Identifies and gives one reason.'),
            tier(
              'Excellent',
              'The hyperbole "a thousand years" is not literal; it conveys the emotional experience of waiting, not the clock time. The reader instantly understands the frustration because the exaggeration communicates feeling more effectively than an accurate number.',
              'Explains the gap between literal and emotional truth.'
            ),
          ],
        },
      ],
      practice: [
        { type: 'mcq', question: '"He ran like the wind" is a:', options: ['Simile', 'Metaphor'], correct: 0 },
        { type: 'mcq', question: '"Bang! The door slammed shut." The word "Bang" is:', options: ['Onomatopoeia', 'Hyperbole'], correct: 0 },
        { type: 'mcq', question: '"The wind tiptoed through the trees" is:', options: ['Personification', 'Alliteration'], correct: 0 },
        { type: 'true-false', question: 'Metaphors usually use the word “like”.', correct: false },
        { type: 'true-false', question: 'A single sentence can contain more than one figurative technique.', correct: true },
      ],
      applicationTask: {
        title: 'Application Task',
        prompt:
          'Read this line: "The rain hammered against the window like an angry drummer." Name the technique or techniques and explain the effect on the reader.',
        sentenceStarter: 'The writer uses...',
        hints: ['Name at least two techniques if you can.', 'Say what feeling is created.', 'Zoom in on the verb “hammered”.'],
      },
      aiFeedback: {
        successCriteria: ['Names the technique or techniques', 'Uses a short quotation', 'Explains the effect on the reader'],
      },
      improvementTask: {
        title: 'Improve Your Answer',
        prompt: 'Improve by zooming in on one word and commenting on its connotations.',
      },
      mastery: {
        title: 'Mastery Challenge',
        prompt: 'Write a single sentence using a metaphor and personification together.',
        xpReward: 50,
      },
    }),

    makeTopic({
      id: 'eng-1-9',
      slug: 'oxymorons-irony-and-sarcasm',
      title: 'Oxymorons, Irony and Sarcasm',
      summary: 'Understand layered meaning through contradiction and indirectness.',
      keywords: ['oxymoron', 'irony', 'sarcasm', 'satire', 'dramatic irony', 'verbal irony', 'situational irony'],
      examSkills: [EXAM_SKILLS.AO1, EXAM_SKILLS.AO2],
      quickCheck: [
        'What is an oxymoron?',
        'What is the difference between irony and sarcasm?',
        'What is dramatic irony?',
      ],
      commonMistakes: [
        'Calling all irony sarcasm',
        'Missing the gap between what is said and what is meant',
        'Identifying an oxymoron but not explaining the contradiction',
      ],
      explainer: {
        title: 'Oxymorons, Irony and Sarcasm',
        content: `
Oxymorons, irony and sarcasm all depend on a gap between what is expected and what is actually meant.

Oxymoron:
Two contradictory words placed together.
Examples:
- "Deafening silence"
- "Bittersweet"
- "Cruel kindness"
- "Living dead"

Effect:
An oxymoron captures complex or mixed feelings that one ordinary word cannot.

Irony:
A gap between expectation and reality.

Three key types:
- Verbal irony: saying the opposite of what you mean
- Dramatic irony: when the audience knows something a character does not
- Situational irony: when the outcome is the opposite of what is expected

Sarcasm:
A specific form of verbal irony used to mock, criticise or show irritation.
Example:
- "Oh brilliant — another detention."

Satire:
A wider technique that uses irony, exaggeration and humour to criticise people, politics or society.

Why writers use these techniques:
- To reveal a character’s real feelings indirectly
- To create humour or tension
- To invite the reader to spot hidden meaning
- To criticise without stating criticism bluntly

These techniques can fail when overused. Too much sarcasm can sound bitter; too much irony can feel clever but emotionally cold.
`,
      },
      interactiveLesson: {
        title: 'Name the Technique',
        steps: [
          { type: 'question', question: '"Deafening silence" is an example of:', options: ['Oxymoron', 'Sarcasm'], correct: 0 },
          { type: 'question', question: '"Oh fantastic, another homework task!" is likely:', options: ['Sarcasm', 'A genuine compliment'], correct: 0 },
          { type: 'question', question: 'A police station being burgled is an example of:', options: ['Situational irony', 'A metaphor'], correct: 0 },
        ],
      },
      workedExamples: [
        {
          label: 'Example 1 — Sarcasm in dialogue',
          prompt:
            '"Well, that went brilliantly," she muttered as the cake slid off the plate and onto the floor.\n\nTask: Identify the technique and its effect.',
          tiers: [
            tier('Weak', 'It is sarcasm.', 'Correct label, no analysis.'),
            tier(
              'Good',
              'This is sarcasm. She says "brilliantly" but the cake has clearly failed, so she means the opposite.',
              'Correct with reasoning.'
            ),
            tier(
              'Excellent',
              'The verbal irony in "brilliantly" works as sarcasm because the word is contradicted by the cake on the floor. The verb "muttered" signals irritation, so the sarcasm lets the reader feel her frustration without her saying “I am frustrated”.',
              'Names the technique, reads the verb choice and links to inference.'
            ),
          ],
        },
        {
          label: 'Example 2 — Dramatic irony',
          prompt:
            'In a play, the audience knows poison is in the cup. The character says: "Let us drink to friendship."\n\nTask: What kind of irony is this and what is its effect?',
          tiers: [
            tier('Weak', 'It is ironic because the drink is poisoned.', 'Right idea but not precise enough.'),
            tier(
              'Good',
              'This is dramatic irony. The audience knows something the character does not, which builds tension.',
              'Correct label and basic effect.'
            ),
            tier(
              'Excellent',
              'This is dramatic irony: the audience holds a crucial piece of knowledge the character lacks. The toast "to friendship" becomes tragic because we know it is also a step towards death. The gap between the character’s innocence and the audience’s awareness creates suspense.',
              'Explains the audience’s role in creating meaning.'
            ),
          ],
        },
        {
          label: 'Example 3 — Oxymoron',
          prompt: '"The war brought a terrible peace."\n\nTask: Identify and explain the technique.',
          tiers: [
            tier('Weak', 'Peace cannot be terrible.', 'Not enough. This notices contradiction but does not analyse it.'),
            tier(
              'Good',
              'This is an oxymoron. "Terrible" and "peace" contradict each other, suggesting the peace was not good.',
              'Correct label with reasoning.'
            ),
            tier(
              'Excellent',
              'The oxymoron "terrible peace" captures a complex idea: the war may have ended, but the cost was so high that peace still feels like suffering. One word could not hold both truths; the contradiction does.',
              'Explains why the contradiction is powerful.'
            ),
          ],
        },
      ],
      practice: [
        { type: 'mcq', question: '"Awfully nice" is:', options: ['Oxymoron', 'Simile'], correct: 0 },
        { type: 'mcq', question: 'A fire alarm going off during a firefighter meeting is:', options: ['Situational irony', 'Metaphor'], correct: 0 },
        { type: 'mcq', question: 'Sarcasm is a specific type of:', options: ['Verbal irony', 'Personification'], correct: 0 },
        { type: 'true-false', question: 'Sarcasm means saying what you truly feel directly.', correct: false },
        { type: 'true-false', question: 'Dramatic irony involves the audience knowing more than the character.', correct: true },
      ],
      applicationTask: {
        title: 'Application Task',
        prompt:
          'Explain how a writer might use sarcasm to reveal a character’s frustration without them saying directly that they are angry.',
        sentenceStarter: 'Sarcasm can reveal frustration because...',
        hints: ['Give a short example.', 'Explain what the reader works out from the tone.', 'Mention the gap between words and meaning.'],
      },
      aiFeedback: {
        successCriteria: ['Explains what sarcasm does', 'Gives a short example', 'Links to reader effect'],
      },
      improvementTask: {
        title: 'Improve Your Answer',
        prompt: 'Include one example that uses punctuation, such as a dash or ellipsis, to signal sarcasm.',
      },
      mastery: {
        title: 'Mastery Challenge',
        prompt: 'Write a line where a character uses irony to show disagreement without saying “I disagree”.',
        xpReward: 50,
      },
    }),

    makeTopic({
      id: 'eng-1-10',
      slug: 'fact-opinion-lists-of-three-and-rhetorical-questions',
      title: 'Fact, Opinion, Lists of Three and Rhetorical Questions',
      summary: 'Master four of the most-used persuasive techniques.',
      keywords: ['fact', 'opinion', 'statistic', 'rhetorical question', 'list of three', 'A FOREST'],
      examSkills: [EXAM_SKILLS.AO2, EXAM_SKILLS.AO5],
      quickCheck: [
        'What is a fact?',
        'What is an opinion?',
        'Why are rhetorical questions persuasive?',
      ],
      commonMistakes: [
        'Treating an opinion as a fact',
        'Using fake statistics without making them plausible in practice writing',
        'Using too many rhetorical questions in a row',
      ],
      explainer: {
        title: 'Fact, Opinion, Lists of Three and Rhetorical Questions',
        content: `
These are essential persuasive techniques. You need to spot them in texts and use them effectively in your own writing.

Fact:
A statement that can be proved.
Example: "Water boils at 100°C."
Facts build authority, especially when they are accurate, relevant and sourced.

Opinion:
A belief or judgement that cannot be proved in the same way.
Example: "Football is the best sport."
Opinions can move readers emotionally, especially when expressed confidently.

Warning:
Opinions can be dressed up as facts. Phrases such as "Everyone knows..." and "It is obvious that..." often present opinion as if it is proven truth.

Statistic:
A numerical fact or claim.
Example: "One in three teenagers feels anxious before bed."
Statistics can make an argument feel authoritative, but they can also be cherry-picked or misleading.

List of three:
Three items, ideas or phrases in a row.
Example: "Safer streets, stronger schools, smarter futures."
Three feels complete and rhythmic. Two can feel thin; four can feel excessive.

Rhetorical question:
A question asked for effect, not for a spoken answer.
Example: "Isn’t it time we did something?"
Rhetorical questions pull the reader into the argument and often make agreement feel obvious.

These techniques sit inside the wider persuasive toolkit called A FOREST:
- Alliteration
- Facts
- Opinions
- Rhetorical questions
- Emotive language
- Statistics
- Three

Strong persuasive writing combines techniques, but it still needs control. Too many techniques can make writing sound forced.
`,
      },
      interactiveLesson: {
        title: 'Identify the Technique',
        steps: [
          { type: 'question', question: '"Every child deserves a safe, fair and loving home." This uses:', options: ['List of three', 'Metaphor'], correct: 0 },
          { type: 'question', question: '"Don’t you think enough is enough?" is a:', options: ['Rhetorical question', 'Fact'], correct: 0 },
          { type: 'question', question: '"Over 2 million people voted this year." is a:', options: ['Statistic', 'Opinion'], correct: 0 },
        ],
      },
      workedExamples: [
        {
          label: 'Example 1 — Combined techniques',
          prompt:
            '"Surely every student deserves a safe, modern and inspiring school? The government spends millions, yet schools are falling apart."\n\nTask: Identify the techniques and their combined effect.',
          tiers: [
            tier('Weak', 'It has a list of three.', 'Misses other techniques.'),
            tier(
              'Good',
              'There is a rhetorical question ("Surely every student...?") and a list of three ("safe, modern and inspiring"). The fact about "millions" contrasts with the opinion that schools are "falling apart".',
              'Multiple techniques named.'
            ),
            tier(
              'Excellent',
              'The rhetorical question assumes the reader agrees before they have fully considered the issue. The list of three ("safe, modern and inspiring") builds rhythmic momentum. The fact about spending "millions" is then set against the emotive claim that schools are "falling apart", framing the issue as public failure rather than neutral information.',
              'Names multiple techniques and explains the rhetorical strategy.'
            ),
          ],
        },
        {
          label: 'Example 2 — Statistic doing the heavy lifting',
          prompt: '"Nine out of ten teachers say workload is unsustainable."\n\nTask: Why is this statistic powerful here?',
          tiers: [
            tier('Weak', 'It is a statistic.', 'No analysis.'),
            tier('Good', 'The statistic "nine out of ten" feels like strong evidence that the problem is real.', 'Correct but brief.'),
            tier(
              'Excellent',
              'The statistic frames the writer’s position as the consensus view. A single complaint can be dismissed, but "nine out of ten" feels like majority reality. The reader is more likely to accept the argument because the number creates authority before the argument develops.',
              'Explains the rhetorical work done by the number.'
            ),
          ],
        },
        {
          label: 'Example 3 — Rhetorical question as a trap',
          prompt: '"Who could possibly argue that our children don’t deserve better?"\n\nTask: Why is this rhetorical question persuasive?',
          tiers: [
            tier('Weak', 'It makes the reader agree.', 'True but underdeveloped.'),
            tier(
              'Good',
              'Only a bad person would say children do not deserve better, so the question pushes the reader into agreement.',
              'Correct logic.'
            ),
            tier(
              'Excellent',
              'The rhetorical question leaves the reader with no socially acceptable answer. To disagree is to appear anti-child. The writer has not yet fully proved their case; the question has done persuasive work by making disagreement feel morally uncomfortable.',
              'Clearly explains the rhetorical pressure created.'
            ),
          ],
        },
      ],
      practice: [
        { type: 'mcq', question: '"The sky is blue during the day" is a:', options: ['Fact', 'Opinion'], correct: 0 },
        { type: 'mcq', question: '"We must act now, today, immediately" is a:', options: ['List of three', 'Simile'], correct: 0 },
        { type: 'mcq', question: '"Cherry pie is the nation’s favourite pudding" without any source is:', options: ['Opinion disguised as fact', 'A reliable statistic'], correct: 0 },
        { type: 'true-false', question: 'A rhetorical question expects a spoken answer.', correct: false },
        { type: 'true-false', question: 'Statistics can be used misleadingly even when technically true.', correct: true },
      ],
      applicationTask: {
        title: 'Application Task',
        prompt: 'Write two sentences persuading a reader to recycle more. Use a rhetorical question and a list of three.',
        sentenceStarter: 'Haven’t we all noticed...',
        hints: ['Open with the rhetorical question.', 'Use the list of three in the second sentence.', 'Keep the tone controlled, not preachy.'],
      },
      aiFeedback: {
        successCriteria: ['Uses a rhetorical question', 'Uses a list of three', 'Sounds persuasive and controlled'],
      },
      improvementTask: {
        title: 'Improve Your Answer',
        prompt: 'Add one statistic, real or invented for practice, to make the argument feel more authoritative.',
      },
      mastery: {
        title: 'Mastery Challenge',
        prompt: 'Which is more powerful: a strong fact or a strong rhetorical question? Give a reason.',
        xpReward: 50,
      },
    }),

    makeTopic({
      id: 'eng-1-11',
      slug: 'narrative',
      title: 'Narrative',
      summary: 'Understand viewpoint, voice and narrative structure.',
      keywords: ['narrative', 'narrator', 'first person', 'third person', 'omniscient', 'unreliable narrator', 'foreshadowing'],
      examSkills: [EXAM_SKILLS.AO1, EXAM_SKILLS.AO2],
      quickCheck: [
        'What is a narrator?',
        'What is first-person narration?',
        'Why might a narrator be unreliable?',
      ],
      commonMistakes: [
        'Confusing narrator with writer',
        'Assuming first person is always reliable',
        'Ignoring structure when analysing narrative',
      ],
      explainer: {
        title: 'Narrative',
        content: `
A narrative is any piece of writing that tells a story. Every narrative has a narrator: the voice telling the story.

Narrative viewpoints:

First person:
Uses "I" or "we". The narrator is usually a character in the story.
Effect: intimate and personal, but limited to what that narrator knows.

Second person:
Uses "you". This is rarer.
Effect: makes the reader feel involved or placed inside the story.

Third person:
Uses "he", "she" or "they". The narrator stands outside the story.

Third-person narrators can be:
- Limited: only knows one character’s thoughts and feelings
- Omniscient: knows everything, including different characters’ thoughts
- Objective: reports actions without entering anyone’s mind

Reliable and unreliable narrators:
- A reliable narrator can broadly be trusted.
- An unreliable narrator may be biased, mistaken, forgetful or dishonest.

Narrative structure:
- Exposition: setting the scene and introducing characters
- Rising action: tension builds
- Climax: peak moment or turning point
- Falling action: consequences unfold
- Resolution: ending or closure

Plot devices:
- Foreshadowing: clues about what will happen later
- Flashback: moving back to an earlier time
- Cliffhanger: ending a section with unresolved tension
- Frame narrative: a story inside another story

Writers choose viewpoint and structure deliberately. Changing either can completely change how a reader experiences the story.
`,
      },
      interactiveLesson: {
        title: 'Identify the Narrator',
        steps: [
          { type: 'question', question: '"I opened the door and stepped into the dark." This narrator is:', options: ['First person', 'Third person'], correct: 0 },
          { type: 'question', question: '"You walk into the room and see a man in the corner." This narrator is:', options: ['Second person', 'First person'], correct: 0 },
          { type: 'question', question: 'A narrator who knows every character’s thoughts is:', options: ['Omniscient', 'Limited'], correct: 0 },
        ],
      },
      workedExamples: [
        {
          label: 'Example 1 — First-person analysis',
          prompt: '"I watched as the letter fell from her hand. I knew what it said before she did."\n\nTask: Identify the viewpoint and its effect.',
          tiers: [
            tier('Weak', 'It is in first person.', 'Correct label, no effect.'),
            tier('Good', 'First person. The pronoun "I" puts the reader inside the narrator’s experience.', 'Basic effect.'),
            tier(
              'Excellent',
              'The first-person narrator places the reader behind the narrator’s eyes. We know what they know — in this case, more than the other character knows — which creates tension. However, we cannot independently check their version of events, so the viewpoint also raises questions about trust.',
              'Explains viewpoint, tension and reliability.'
            ),
          ],
        },
        {
          label: 'Example 2 — Unreliable narrator',
          prompt: '"I would never lie about something so important. And besides — who can prove it?"\n\nTask: How do we know this narrator may be unreliable?',
          tiers: [
            tier('Weak', 'They sound shifty.', 'Intuition but no textual clue.'),
            tier(
              'Good',
              'The narrator insists they would never lie, but then adds "who can prove it?" which is exactly what a liar might say.',
              'Good use of evidence.'
            ),
            tier(
              'Excellent',
              'The protest "I would never lie" feels suspicious because it is so defensive. The shift to "who can prove it?" moves from truth to evidence, suggesting the narrator cares more about escaping blame than being honest. The reader is invited to distrust them.',
              'Identifies the denial, the shift in logic and the reader effect.'
            ),
          ],
        },
      ],
      practice: [
        { type: 'mcq', question: 'Which pronoun is used in second-person narrative?', options: ['You', 'I'], correct: 0 },
        { type: 'mcq', question: 'A narrator who reports only actions, never thoughts, is:', options: ['Third-person objective', 'Omniscient'], correct: 0 },
        { type: 'mcq', question: 'A clue about what will happen later is called:', options: ['Foreshadowing', 'Flashback'], correct: 0 },
        { type: 'true-false', question: 'A third-person narrator is usually a character in the story.', correct: false },
        { type: 'true-false', question: 'Narratives usually have some kind of beginning, middle and end.', correct: true },
      ],
      applicationTask: {
        title: 'Application Task',
        prompt:
          'Explain one advantage of a first-person narrator and one advantage of an omniscient third-person narrator. Use an example if you can.',
        sentenceStarter: 'A first-person narrator is useful because...',
        hints: ['Think about what the reader sees or does not see.', 'Use the word “viewpoint”.', 'Mention trust or reliability if possible.'],
      },
      aiFeedback: {
        successCriteria: ['Identifies one benefit of first person', 'Identifies one benefit of omniscient narration', 'Gives at least one example'],
      },
      improvementTask: {
        title: 'Improve Your Answer',
        prompt: 'Add one line explaining why an unreliable narrator might create more interest than a reliable one.',
      },
      mastery: {
        title: 'Mastery Challenge',
        prompt: 'Rewrite this first-person sentence in the third person: "I pushed the door open and froze."',
        xpReward: 50,
      },
    }),

    makeTopic({
      id: 'eng-1-12',
      slug: 'context',
      title: 'Context',
      summary: 'Use background knowledge to read texts more deeply.',
      keywords: ['context', 'historical context', 'social context', 'cultural context', 'biographical context', 'textual context'],
      examSkills: [EXAM_SKILLS.AO1, EXAM_SKILLS.AO2, EXAM_SKILLS.AO3],
      quickCheck: [
        'What is historical context?',
        'What is biographical context?',
        'Why should context not become a history dump?',
      ],
      commonMistakes: [
        'Dropping in context without linking it to a quotation',
        'Judging older texts only by modern values',
        'Writing lots of background but little analysis',
      ],
      explainer: {
        title: 'Context',
        content: `
Context is the background of a text: the world it was written in, the world it describes, and sometimes the writer’s own life.

Types of context include:

Historical context:
The time period, such as Victorian Britain, the inter-war years or the 1960s.

Social context:
Class, gender, family, poverty, education and social expectations.

Cultural context:
Beliefs, customs, religion, traditions and shared values.

Political context:
War, empire, elections, revolution, protest or law.

Literary context:
Genre conventions, literary movements and what kind of writing was popular.

Biographical context:
The writer’s own life and experiences.

Textual context:
The surrounding scene, chapter, stanza or whole text.

Why context matters:
- A Victorian novel about marriage makes more sense when we know women had fewer independent routes to financial security.
- A Christmas Carol becomes sharper when we understand Victorian poverty and attitudes to the poor.
- A modern climate change article is shaped by 21st-century environmental concern.

How to use context well:
- Do not dump background knowledge.
- Link context to a specific word, scene, character or structural choice.
- Use context to explain meaning, not replace analysis.
- One precise contextual point is better than three vague ones.

Context should act like a lens. It helps us see the text more clearly.
`,
      },
      interactiveLesson: {
        title: 'Context in Action',
        steps: [
          { type: 'question', question: 'Why might a Victorian novel present strict rules for women?', options: ['Because that was part of the social context', 'Because all writers enjoyed strict rules'], correct: 0 },
          { type: 'question', question: 'Knowing a writer lived through a war could be part of their:', options: ['Biographical context', 'Opinion'], correct: 0 },
          { type: 'question', question: 'Historical context tells us about:', options: ['The time and circumstances around a text', 'Only the main character'], correct: 0 },
        ],
      },
      workedExamples: [
        {
          label: 'Example 1 — Context and a scene',
          prompt:
            'A Victorian novel shows a young woman pressured to marry a wealthy man she does not love.\n\nTask: Use context to deepen your analysis.',
          tiers: [
            tier('Weak', 'This was bad.', 'Modern judgement without context.'),
            tier(
              'Good',
              'In Victorian Britain, women often depended on marriage for security, so the pressure would feel realistic to contemporary readers.',
              'Simple use of social context.'
            ),
            tier(
              'Excellent',
              'Victorian women had limited independent routes to financial security, so marriage was often an economic decision as well as a romantic one. By staging this pressure, the writer makes the young woman’s “choice” appear like no choice at all, creating a critique sharpened by its historical context.',
              'Uses context to reveal the writer’s possible criticism.'
            ),
          ],
        },
        {
          label: 'Example 2 — Biographical context',
          prompt:
            'A poet survived a long illness as a child. Their work constantly returns to images of breath, stillness and morning.\n\nTask: How might biographical context inform your reading?',
          tiers: [
            tier('Weak', 'They were ill so they write about it.', 'Stops at biography.'),
            tier(
              'Good',
              'Because the poet survived illness, the images of breath and morning may represent gratitude for being alive.',
              'Uses biography carefully.'
            ),
            tier(
              'Excellent',
              'Knowing the poet survived serious childhood illness, the recurring images of breath, stillness and morning carry added weight: they may turn ordinary survival into something precious. Context does not replace close reading, but it sharpens which images matter and why.',
              'Uses biography as a lens, not the whole answer.'
            ),
          ],
        },
      ],
      practice: [
        { type: 'mcq', question: 'A modern text about climate change reflects its:', options: ['Social and cultural context today', 'Historical context only'], correct: 0 },
        { type: 'mcq', question: 'A writer’s personal experiences are part of:', options: ['Biographical context', 'Historical context only'], correct: 0 },
        { type: 'mcq', question: 'Surrounding scenes or chapters give:', options: ['Textual context', 'Political context'], correct: 0 },
        { type: 'true-false', question: 'Context only matters for very old texts.', correct: false },
        { type: 'true-false', question: 'Strong analysis uses context sparingly and specifically.', correct: true },
      ],
      applicationTask: {
        title: 'Application Task',
        prompt: 'Explain why knowing the context of a text might change how a reader understands it. Use one example.',
        sentenceStarter: 'Context helps a reader because...',
        hints: ['Use the phrase “shaped by their time”.', 'Use a specific example.', 'Link context to a writer’s choice.'],
      },
      aiFeedback: {
        successCriteria: ['Explains context clearly', 'Gives one specific example', 'Links context to reader understanding'],
      },
      improvementTask: {
        title: 'Improve Your Answer',
        prompt: 'Add one line on the risk of overusing context instead of analysing the text.',
      },
      mastery: {
        title: 'Mastery Challenge',
        prompt: 'Why might two readers from different eras understand the same text differently?',
        xpReward: 50,
      },
    }),

    makeTopic({
      id: 'eng-1-13',
      slug: 'grammar-and-punctuation',
      title: 'Grammar and Punctuation',
      summary: 'The building blocks of clear writing.',
      keywords: ['grammar', 'punctuation', 'simple sentence', 'compound sentence', 'complex sentence', 'semicolon', 'colon', 'apostrophe'],
      examSkills: [EXAM_SKILLS.AO5, EXAM_SKILLS.AO6],
      quickCheck: [
        'What is a simple sentence?',
        'What is a complex sentence?',
        'What are the two main uses of apostrophes?',
      ],
      commonMistakes: [
        'Comma splicing two full sentences',
        'Confusing its and it’s',
        'Using semicolons where a colon is needed',
      ],
      explainer: {
        title: 'Grammar and Punctuation',
        content: `
Grammar is the system that builds meaningful sentences. Punctuation is the set of marks that organises meaning and rhythm.

Key sentence types:

Simple sentence:
One main clause.
Example: "She ran."

Compound sentence:
Two main clauses joined by a coordinating conjunction such as and, but, so or or.
Example: "She ran and she fell."

Complex sentence:
A main clause plus a subordinate clause.
Example: "She ran, even though she was tired."

Compound-complex sentence:
Two main clauses plus at least one subordinate clause.
Example: "She ran, even though she was tired, and she still won."

Key punctuation marks:

Full stop:
Closes a sentence.

Comma:
Separates items, clauses or extra information.

Question mark:
Closes a direct question.

Exclamation mark:
Shows strong feeling. Use sparingly.

Apostrophe:
Shows missing letters in contractions, such as "don’t", or possession, such as "the dog’s bowl".

Semicolon:
Joins two closely related main clauses.

Colon:
Introduces a list, quotation or explanation.

Dash:
Creates emphasis or a break in thought.

Ellipsis:
Suggests a pause, trailing off or missing text.

Brackets:
Add extra, non-essential information.

Hyphen:
Joins words, such as "state-of-the-art".

Inverted commas:
Mark direct speech or quotations.

Active and passive voice:

Active:
"The dog bit the postman."
Clear and direct.

Passive:
"The postman was bitten by the dog."
Can sound formal or shift focus away from the doer.

Strong writers choose grammar and punctuation deliberately: for clarity, meaning, emphasis and rhythm.
`,
      },
      interactiveLesson: {
        title: 'Grammar Check',
        steps: [
          { type: 'question', question: 'Which is a simple sentence?', options: ['"She ran."', '"She ran because she was late."'], correct: 0 },
          { type: 'question', question: 'Which punctuation mark joins two related main clauses?', options: ['Semicolon', 'Comma'], correct: 0 },
          { type: 'question', question: 'Which is active voice?', options: ['"The dog bit the postman."', '"The postman was bitten by the dog."'], correct: 0 },
        ],
      },
      workedExamples: [
        {
          label: 'Example 1 — Punctuating a sentence',
          prompt: '"the boy ran home he was late"\n\nTask: Punctuate correctly and explain your choices.',
          tiers: [
            tier('Weak', 'The boy ran home he was late.', 'Adds a capital letter but leaves a run-on sentence.'),
            tier('Good', 'The boy ran home. He was late.', 'Two simple sentences; grammatical and clear.'),
            tier(
              'Excellent',
              'The boy ran home; he was late. The semicolon shows that the two main clauses are closely linked: his lateness explains the running. A full stop would also work, but the semicolon is a more precise choice.',
              'Chooses a correct option and explains why.'
            ),
          ],
        },
        {
          label: 'Example 2 — Using a colon',
          prompt: '"I knew exactly what I wanted ___ to leave."\n\nTask: Replace the blank with a punctuation mark and explain.',
          tiers: [
            tier('Weak', 'A comma.', 'This creates weak punctuation and does not clearly introduce the explanation.'),
            tier('Good', 'A colon: "I knew exactly what I wanted: to leave." The colon introduces the explanation.', 'Correct colon use.'),
            tier(
              'Excellent',
              'A colon is the strongest choice: "I knew exactly what I wanted: to leave." The colon signals that what follows explains the main clause. A dash could also work for a more dramatic feel, but a comma would be weaker.',
              'Names the best choice and compares alternatives.'
            ),
          ],
        },
        {
          label: 'Example 3 — Apostrophe possession vs contraction',
          prompt: '"its getting late but the dogs bowl is still full"\n\nTask: Correct the apostrophes.',
          tiers: [
            tier('Weak', 'It’s getting late but the dogs bowl is still full.', 'Fixes one apostrophe but misses possession.'),
            tier('Good', 'It’s getting late but the dog’s bowl is still full.', 'Correct.'),
            tier(
              'Excellent',
              'It’s getting late, but the dog’s bowl is still full. "It’s" means "it is", so it needs an apostrophe for missing letters. "Dog’s" shows the bowl belongs to one dog. If several dogs shared the bowl, it would be "dogs’ bowl".',
              'Corrects and teaches the rule.'
            ),
          ],
        },
      ],
      practice: [
        { type: 'mcq', question: 'Which sentence uses a comma correctly?', options: ['"I bought apples, oranges and pears."', '"I bought apples oranges, and pears."'], correct: 0 },
        { type: 'mcq', question: 'Which correctly contracts "do not"?', options: ['don’t', 'dont'], correct: 0 },
        { type: 'mcq', question: 'A colon is usually used to:', options: ['Introduce a list or explanation', 'Join a comma splice'], correct: 0 },
        { type: 'true-false', question: 'A semicolon is used to link two closely related main clauses.', correct: true },
        { type: 'true-false', question: 'Passive voice is always wrong.', correct: false },
      ],
      applicationTask: {
        title: 'Application Task',
        prompt: 'Rewrite this line with correct punctuation: "my mum said we had to leave but i didnt want to"',
        sentenceStarter: 'My mum said...',
        hints: ['Check capital letters.', 'Check the apostrophe.', 'Decide whether you need a comma, full stop or semicolon.'],
      },
      aiFeedback: {
        successCriteria: ['Correct capital letters', 'Correct end punctuation', 'Correct apostrophes'],
      },
      improvementTask: {
        title: 'Improve Your Answer',
        prompt: 'Rewrite it again using a semicolon or colon to control the relationship between the ideas.',
      },
      mastery: {
        title: 'Mastery Challenge',
        prompt: 'Write one simple, one compound and one complex sentence about your school day.',
        xpReward: 50,
      },
    }),

    makeTopic({
      id: 'eng-1-14',
      slug: 'warm-up-and-worked-practice-questions-fiction-non-fiction',
      title: 'Warm-Up and Worked Practice Questions',
      summary: 'Short, scaffolded questions to build confidence.',
      keywords: ['warm-up', 'worked practice', 'PEE', 'evidence', 'reader effect', 'analysis'],
      examSkills: [EXAM_SKILLS.AO1, EXAM_SKILLS.AO2],
      quickCheck: [
        'What does PEE stand for?',
        'Why should quotations be short?',
        'What is a reader effect?',
      ],
      commonMistakes: [
        'Skipping planning because the question looks short',
        'Writing about the whole extract instead of the question',
        'Forgetting to explain the effect on the reader',
      ],
      explainer: {
        title: 'Warm-Up and Worked Practice Questions',
        content: `
Warm-up questions prepare you for longer exam-style tasks. They build speed, accuracy and confidence.

Warm-up questions usually focus on:
- Identifying fiction and non-fiction
- Spotting purpose and audience
- Naming techniques
- Making quick inferences
- Commenting briefly on effect

Worked practice questions show you how a strong answer is built.

A strong short answer usually includes:
- A clear point
- A short quotation
- An explanation of effect
- A link back to the question

Use PEE:
- Point: say what the writer does or suggests
- Evidence: give a short quotation
- Explain: say how the evidence creates meaning or affects the reader

Before answering:
1. Read the question carefully.
2. Highlight the key words in the question.
3. Choose one or two short quotations.
4. Decide what each quotation proves.
5. Write precisely.

Warm-ups are not just for weaker students. They sharpen the habits needed for high-grade answers.
`,
      },
      interactiveLesson: {
        title: 'Warm-Up Questions',
        steps: [
          { type: 'question', question: 'A recipe is:', options: ['Non-fiction', 'Fiction'], correct: 0 },
          { type: 'question', question: 'The purpose of an advert is mainly to:', options: ['Persuade', 'Report facts neutrally'], correct: 0 },
          { type: 'question', question: 'A rhetorical question is used to:', options: ['Make the reader think', 'Get a spoken reply'], correct: 0 },
        ],
      },
      workedExamples: [
        {
          label: 'Example 1 — Short analytical response',
          prompt:
            'Text: "The rain hammered against the window, and the wind whispered through the cracks."\n\nQuestion: How does the writer create atmosphere?',
          tiers: [
            tier('Weak', 'The rain is loud.', 'Retells the line without analysis.'),
            tier('Good', 'Personification ("the wind whispered") makes the setting feel alive and eerie.', 'One technique and effect.'),
            tier(
              'Excellent',
              'The writer combines personification ("the wind whispered") with the forceful verb "hammered" to create a tense atmosphere. The rain feels aggressive while the wind seems sly and secretive. Together, they make the reader feel surrounded by threatening weather.',
              'Two techniques, linked mood, reader effect.'
            ),
          ],
        },
        {
          label: 'Example 2 — Mini PEE',
          prompt:
            'Text: "Every footstep echoed in the empty street."\n\nQuestion: How does the writer create a sense of loneliness?',
          tiers: [
            tier('Weak', 'It is empty so it is lonely.', 'Paraphrase, no evidence.'),
            tier('Good', 'The word "empty" creates loneliness, and "echoed" makes the street sound quiet and eerie.', 'Evidence and effect.'),
            tier(
              'Excellent',
              'The writer creates loneliness through the contrast between sound and silence: "every footstep echoed" implies there is nothing else to cover the noise. The single word "empty" deepens the isolation because the street is not only quiet but abandoned.',
              'Contrast, zoomed word, double effect.'
            ),
          ],
        },
      ],
      practice: [
        { type: 'mcq', question: 'Which word from "the wind whispered" creates personification?', options: ['whispered', 'wind'], correct: 0 },
        { type: 'mcq', question: 'The verb "hammered" suggests the rain is:', options: ['Violent', 'Gentle'], correct: 0 },
        { type: 'mcq', question: 'A short embedded quotation is usually:', options: ['Stronger than a long copied sentence', 'Weaker than a long copied sentence'], correct: 0 },
        { type: 'true-false', question: 'Quotations should be used when analysing a text.', correct: true },
        { type: 'true-false', question: 'Warm-up questions are only for weaker students.', correct: false },
      ],
      applicationTask: {
        title: 'Application Task',
        prompt:
          'Read: "The street was empty, and every footstep echoed." Write 2–3 sentences on how the writer creates atmosphere.',
        sentenceStarter: 'The writer creates atmosphere by...',
        hints: ['Use a short quotation.', 'Name the mood.', 'Explain the reader effect.'],
      },
      aiFeedback: {
        successCriteria: ['Uses a short quotation', 'Explains what the reader feels', 'Names a technique or method'],
      },
      improvementTask: {
        title: 'Improve Your Answer',
        prompt: 'Add one sentence showing how the atmosphere feels more unsettling than simply sad.',
      },
      mastery: {
        title: 'Mastery Challenge',
        prompt: 'What makes an atmosphere “tense” rather than “calm” in writing?',
        xpReward: 50,
      },
    }),

    makeTopic({
      id: 'eng-1-15',
      slug: 'worked-practice-questions-fiction-non-fiction',
      title: 'Worked Practice Questions',
      summary: 'Longer modelled answers for fiction and non-fiction tasks.',
      keywords: ['model answer', 'worked example', 'analysis paragraph', 'evidence', 'zoom in'],
      examSkills: [EXAM_SKILLS.AO1, EXAM_SKILLS.AO2],
      quickCheck: [
        'What should the first sentence of an analytical paragraph do?',
        'Why is zooming in on one word useful?',
        'What should the final sentence link back to?',
      ],
      commonMistakes: [
        'Copying a model answer without understanding its structure',
        'Starting with evidence instead of a point',
        'Using a technique name without explaining why it matters',
      ],
      explainer: {
        title: 'Worked Practice Questions',
        content: `
A worked practice question shows a full model answer. Your job is to read it like an engineer reads a machine: notice what each part is doing.

Look for:
- The overview sentence
- The point at the start of each paragraph
- The short embedded evidence
- The explanation of reader effect
- The zoom-in on a key word
- The final link back to the question

Strong answers usually:
- Make a point first
- Quote second
- Explain third
- Use one precise technical term at a time
- Zoom in on a single word
- Link to the writer’s purpose or reader effect

A useful method:
1. Read the model answer.
2. Highlight the point in one colour.
3. Highlight the quotation in another colour.
4. Underline the explanation.
5. Circle any technical terms.
6. Close the model and try writing your own version.

The gap between your version and the model shows you exactly what to improve.
`,
      },
      interactiveLesson: {
        title: 'Learn From Models',
        steps: [
          { type: 'question', question: 'What is the first part of a strong analytical answer?', options: ['Make a point', 'Give background'], correct: 0 },
          { type: 'question', question: 'After the point, you should add:', options: ['Evidence', 'A conclusion only'], correct: 0 },
          { type: 'question', question: 'The explanation should focus on:', options: ['The effect on the reader', 'The length of the text'], correct: 0 },
        ],
      },
      workedExamples: [
        {
          label: 'Example 1 — Fiction analysis',
          prompt:
            'Text: "Sam clenched his fists. His voice was calm, but his eyes gave him away."\n\nQuestion: What impression do we get of Sam?',
          tiers: [
            tier('Weak', 'Sam is angry.', 'Inference without evidence.'),
            tier(
              'Good',
              'Sam is trying to hide his anger. "Clenched his fists" shows he is tense, and his eyes "gave him away".',
              'Simple PEE structure.'
            ),
            tier(
              'Excellent',
              'The writer creates the impression of a man holding himself together. The clenched fists and "calm" voice work against the revealing eyes: Sam is performing control, but his body betrays him. The contrast between what he says and what he shows invites the reader to feel the effort it costs him.',
              'Contrast, zoomed detail and reader effect.'
            ),
          ],
        },
        {
          label: 'Example 2 — Non-fiction analysis',
          prompt:
            'Text: "We, the children of this town, have waited long enough."\n\nQuestion: How does the writer make the argument feel urgent?',
          tiers: [
            tier('Weak', 'It sounds serious.', 'Feeling without evidence.'),
            tier(
              'Good',
              'The direct address "we, the children" makes the speaker part of a group, and "waited long enough" sounds like patience has run out.',
              'Two methods identified.'
            ),
            tier(
              'Excellent',
              'The collective first person ("we, the children") gives the speaker authority through numbers: they claim to speak for many. "Waited long enough" frames the past as already too patient, turning the present into a moment for action. The argument feels urgent because the sentence suggests delay has become unacceptable.',
              'Names the collective-pronoun strategy and urgency.'
            ),
          ],
        },
      ],
      practice: [
        { type: 'mcq', question: 'Which phrase from the fiction example shows tension?', options: ['"clenched his fists"', '"calm voice"'], correct: 0 },
        { type: 'mcq', question: 'The contrast between calm voice and revealing eyes creates:', options: ['Hidden conflict', 'Peacefulness'], correct: 0 },
        { type: 'mcq', question: 'The collective "we" in the non-fiction example creates:', options: ['Authority through numbers', 'Humour'], correct: 0 },
        { type: 'true-false', question: 'A worked answer should explain the effect on the reader.', correct: true },
        { type: 'true-false', question: 'Strong analytical answers should only retell what happens.', correct: false },
      ],
      applicationTask: {
        title: 'Application Task',
        prompt: 'Using the fiction extract about Sam, write your own short analytical paragraph. Use PEE and at least one technical term.',
        sentenceStarter: 'Sam appears...',
        hints: ['Use a short quotation.', 'Comment on the contrast between voice and eyes.', 'Zoom in on one word.'],
      },
      aiFeedback: {
        successCriteria: ['Makes a clear point', 'Uses a short quotation', 'Explains the effect on the reader'],
      },
      improvementTask: {
        title: 'Improve Your Answer',
        prompt: 'Zoom in on one word or phrase, such as "gave him away", and comment on its connotations.',
      },
      mastery: {
        title: 'Mastery Challenge',
        prompt: 'Why is it stronger to say “This suggests...” rather than just “This is...”?',
        xpReward: 50,
      },
    }),

    makeTopic({
      id: 'eng-1-16',
      slug: 'practice-questions-fiction-non-fiction',
      title: 'Practice Questions',
      summary: 'Independent practice across fiction and non-fiction.',
      keywords: ['independent practice', 'exam practice', 'analysis', 'planning', 'evidence'],
      examSkills: [EXAM_SKILLS.AO1, EXAM_SKILLS.AO2, EXAM_SKILLS.AO5, EXAM_SKILLS.AO6],
      quickCheck: [
        'What should you do before writing?',
        'Why is relevance more important than length?',
        'How many quotations should a short paragraph usually use?',
      ],
      commonMistakes: [
        'Starting without planning',
        'Choosing evidence that does not answer the question',
        'Writing too much and losing focus',
      ],
      explainer: {
        title: 'Practice Questions',
        content: `
This is where theory becomes skill. You will attempt tasks without a model directly in front of you.

Before you write:
- Read the question twice.
- Read the extract twice: once for meaning, once for methods.
- Underline 2–3 short phrases.
- Plan one point per quotation.
- Decide the mood, attitude or argument you want to explain.

When you write:
- Start each paragraph with a clear point.
- Embed your short quotation.
- Explain the effect on the reader.
- Link to the writer’s purpose if you can.
- Keep every sentence relevant to the question.

After you write:
- Re-read your answer.
- Cut any sentence that does not answer the question.
- Check punctuation.
- Check that each paragraph has a clear point.

Examiners reward precision more than length. A tight paragraph of five focused sentences is usually stronger than a long paragraph that wanders.
`,
      },
      interactiveLesson: {
        title: 'Quick Practice',
        steps: [
          { type: 'question', question: 'Before writing an analytical answer, you should:', options: ['Plan', 'Just start writing'], correct: 0 },
          { type: 'question', question: 'Each analytical paragraph should start with a:', options: ['Clear point', 'Long quotation'], correct: 0 },
          { type: 'question', question: 'Strong analysis focuses on:', options: ['Effect on the reader', 'Counting words'], correct: 0 },
        ],
      },
      workedExamples: [
        {
          label: 'Example 1 — Fiction setting',
          prompt:
            'Text: "The station was crowded, noisy and full of people rushing past with their eyes down."\n\nQuestion: How does the writer describe the station?',
          tiers: [
            tier('Weak', 'It is busy.', 'Retells.'),
            tier(
              'Good',
              'The list of three "crowded, noisy and full of people rushing past" shows how busy it is. "Eyes down" makes people feel disconnected.',
              'Technique plus evidence.'
            ),
            tier(
              'Excellent',
              'The list of three layers the image of the station and mimics the overwhelming sensory load. The final detail, "eyes down", reveals an emotional cost: the station is not just busy but lonely. The reader feels the strange isolation of a place full of people.',
              'Analyses sensory layering and emotional effect.'
            ),
          ],
        },
        {
          label: 'Example 2 — Non-fiction argument',
          prompt:
            'Text: "Our high street is tired, empty and forgotten — but it does not have to stay that way."\n\nQuestion: How does the writer build an argument?',
          tiers: [
            tier('Weak', 'The writer wants to change the high street.', 'Paraphrase.'),
            tier(
              'Good',
              'The list of three ("tired, empty and forgotten") creates a bleak picture, and the dash signals a turn to hope.',
              'Technique and structure.'
            ),
            tier(
              'Excellent',
              'The list of three builds a bleak crescendo, with each adjective making the high street seem more neglected. The dash creates a structural turn: the picture is negative, but "it does not have to stay that way" offers hope. The writer makes the problem feel urgent first, then presents change as possible.',
              'Recognises the structure of the argument.'
            ),
          ],
        },
      ],
      practice: [
        { type: 'mcq', question: 'The list of three creates a sense of:', options: ['Overwhelming movement', 'Calm stillness'], correct: 0 },
        { type: 'mcq', question: 'Why does the writer use "eyes down"?', options: ['To suggest people feel disconnected', 'To describe fashion'], correct: 0 },
        { type: 'mcq', question: 'The dash in the high-street example acts as a:', options: ['Turning point', 'List marker'], correct: 0 },
        { type: 'true-false', question: 'Good practice answers always quote the whole text.', correct: false },
        { type: 'true-false', question: 'Relevance to the question is more important than length.', correct: true },
      ],
      applicationTask: {
        title: 'Application Task',
        prompt:
          'Read this line: "The classroom was silent, and the clock ticked louder than ever." Write a short paragraph analysing the atmosphere.',
        sentenceStarter: 'The writer creates a tense atmosphere by...',
        hints: ['Use a short quotation.', 'Comment on sound contrast.', 'Zoom in on “louder”.'],
      },
      aiFeedback: {
        successCriteria: ['Clear point', 'Short quotation', 'Explanation of reader effect'],
      },
      improvementTask: {
        title: 'Improve Your Answer',
        prompt: 'Add one line zooming in on the word "louder" and why the clock feels bigger than it really is.',
      },
      mastery: {
        title: 'Mastery Challenge',
        prompt: 'Write a short paragraph describing a place you know well, creating a specific atmosphere: peaceful, tense or lonely.',
        xpReward: 50,
      },
    }),

    makeTopic({
      id: 'eng-1-17',
      slug: 'understanding-fiction-and-non-fiction-knowledge-organiser',
      title: 'Understanding Fiction & Non-Fiction — Knowledge Organiser',
      summary: 'Fast-reference summary for Section 1.',
      keywords: ['knowledge organiser', 'revision', 'terminology', 'retrieval', 'summary'],
      examSkills: [EXAM_SKILLS.AO1, EXAM_SKILLS.AO2, EXAM_SKILLS.AO5, EXAM_SKILLS.AO6],
      quickCheck: [
        'What does inference mean?',
        'What is the difference between tone and register?',
        'What does PEE stand for?',
      ],
      commonMistakes: [
        'Revising definitions without practising application',
        'Forgetting to use terminology in written answers',
        'Knowing techniques but not explaining effects',
      ],
      explainer: {
        title: 'Knowledge Organiser',
        content: `
Use this summary to revise everything from Section 1.

Text types:
- Fiction = invented.
- Non-fiction = based on reality.
- Forms to know: novel, short story, poem, play, biography, autobiography, article, letter, diary, speech, leaflet, review, report and blog.

Purpose and audience:
- PAPER: Persuade, Argue, Inform, Entertain, Advise.
- Also know: Describe and Explain.
- Audience affects vocabulary, tone, layout, length and examples.

Language:
- Inference = meaning worked out from clues.
- Explicit = directly stated.
- Implicit = suggested.
- Connotation = the feelings or associations a word carries.

Layout:
- Titles, subheadings, columns, bullets, captions, pull-quotes, images, bold, italics, colour and white space.

Tone, register and style:
- Tone = attitude.
- Register = level of formality.
- Style = writer’s overall fingerprint.
- Informal features: contractions, slang, direct address, rhetorical questions, short sentences.
- Formal features: Standard English, no slang, precise vocabulary, modal verbs, careful structure.

Figurative language:
- Simile: comparison using like or as.
- Metaphor: saying one thing is another.
- Personification: giving human qualities to non-human things.
- Imagery: language appealing to the senses.
- Hyperbole: deliberate exaggeration.
- Alliteration: repeated opening sounds.
- Onomatopoeia: sound words.

Layered meaning:
- Oxymoron: contradictory words together.
- Irony: gap between expectation and reality.
- Sarcasm: mocking verbal irony.
- Satire: humorous criticism of society, politics or people.

Persuasive techniques:
A FOREST:
- Alliteration
- Facts
- Opinions
- Rhetorical questions
- Emotive language
- Statistics
- Three/list of three

Narrative:
- First person, second person, third person.
- Third person can be limited, omniscient or objective.
- Reliable and unreliable narrators.
- Structure: exposition, rising action, climax, falling action, resolution.
- Devices: foreshadowing, flashback, cliffhanger, frame narrative.

Context:
- Historical, social, cultural, political, literary, biographical and textual context.
- Use context to explain a writer’s choice, not as a background dump.

Grammar and punctuation:
- Sentence types: simple, compound, complex, compound-complex.
- Key marks: full stop, comma, question mark, exclamation mark, apostrophe, semicolon, colon, dash, ellipsis, brackets, hyphen and inverted commas.
- Active voice is direct.
- Passive voice shifts focus.

Answering questions:
- Point → Evidence → Explain.
- Use short embedded quotations.
- Zoom in on one word.
- Link to the reader, mood, tone or writer’s purpose.
`,
      },
      interactiveLesson: {
        title: 'Recall Quiz',
        steps: [
          { type: 'question', question: 'What does inference mean?', options: ['Working out ideas from clues', 'Describing a setting'], correct: 0 },
          { type: 'question', question: 'A list of three is used for:', options: ['Emphasis and rhythm', 'Quieting the reader'], correct: 0 },
          { type: 'question', question: 'Formal style usually avoids:', options: ['Contractions and slang', 'Evidence'], correct: 0 },
        ],
      },
      workedExamples: [
        {
          label: 'Example 1 — Short combined analysis',
          prompt:
            '"The city roared, its lights dazzling the visitors who had never seen anything like it."\n\nTask: Which techniques combine here?',
          tiers: [
            tier('Weak', 'There is imagery.', 'Only one technique, not analysed.'),
            tier(
              'Good',
              'Personification ("the city roared") and imagery ("lights dazzling") make the city feel alive.',
              'Two techniques identified.'
            ),
            tier(
              'Excellent',
              'Personification ("the city roared") turns the city into a living creature. Visual imagery ("lights dazzling") floods the reader’s vision, while the reference to visitors who had "never seen anything like it" suggests awe and unfamiliarity. The techniques combine to make the city feel overwhelming.',
              'Layered techniques with reader effect.'
            ),
          ],
        },
        {
          label: 'Example 2 — Mixed persuasive techniques',
          prompt:
            '"Surely we — every parent, every child, every teacher — deserve a school system that actually works?"\n\nTask: Which techniques does the writer combine?',
          tiers: [
            tier('Weak', 'Rhetorical question.', 'Only identifies one technique.'),
            tier(
              'Good',
              'The writer uses a rhetorical question, collective pronoun "we", and a list of three ("every parent, every child, every teacher").',
              'Three techniques identified.'
            ),
            tier(
              'Excellent',
              'The rhetorical question pressures the reader to agree, while the collective "we" creates shared identity. The list of three ("every parent, every child, every teacher") claims the whole school community. Together, these techniques make disagreement seem unreasonable.',
              'Names techniques and explains the overall persuasive effect.'
            ),
          ],
        },
      ],
      practice: [
        { type: 'mcq', question: 'Which technique is in "the city roared"?', options: ['Personification', 'Oxymoron'], correct: 0 },
        { type: 'mcq', question: 'An opinion is a:', options: ['Belief or judgement', 'Provable fact'], correct: 0 },
        { type: 'mcq', question: 'A writer’s fingerprint across many pieces is their:', options: ['Style', 'Register only'], correct: 0 },
        { type: 'true-false', question: 'Context helps a reader understand a text more deeply.', correct: true },
        { type: 'true-false', question: 'Hyperbole is deliberate exaggeration for effect.', correct: true },
      ],
      applicationTask: {
        title: 'Application Task',
        prompt: 'Pick any three ideas from Section 1 and explain how each one would improve your analytical writing.',
        sentenceStarter: 'Firstly,...',
        hints: ['Use at least one technique name.', 'Say what each idea helps you do as a reader or writer.', 'Give one example if possible.'],
      },
      aiFeedback: {
        successCriteria: ['Chooses three clear ideas', 'Explains why each idea helps', 'Uses accurate terminology'],
      },
      improvementTask: {
        title: 'Improve Your Answer',
        prompt: 'Add one line explaining which idea you find hardest to apply and what you will do to improve.',
      },
      mastery: {
        title: 'Mastery Challenge',
        prompt: 'Write a short paragraph analysing a text of your choice. Use at least two techniques and one short quotation.',
        xpReward: 50,
      },
    }),
  ],
};

export const getEnglishContent1TopicCount = () => englishContent1.topics.length;

export const getEnglishContent1TopicBySlug = (slug) =>
  englishContent1.topics.find((topic) => topic.slug === slug);

export const getEnglishContent1TopicById = (id) =>
  englishContent1.topics.find((topic) => topic.id === id);

export const getEnglishContent1Keywords = () =>
  Array.from(new Set(englishContent1.topics.flatMap((topic) => topic.keywords || []))).sort();

export default englishContent1;